<template>
  <section>
    <b-alert show v-if="!currentUser">
      <p>
        <em class="fa fa-info-circle mr-2"></em>
        This form allows you to add users, setting them up for roles and access before they
        first log in. Access is based on Pfizer SSO, and you are not required to assign
        password credentials.
      </p></b-alert
    >
    <div>
      <b-form
        class="p-2"
        @submit="onSubmit"
        :method="'POST'"
        :action="action"
      >
        <input type="hidden" name="_token" :value="csrf" />
        <input type="hidden" name="_method" :value="method" />
        <input type="hidden" name="id" :value="currentUser ? currentUser.id : ''" />
        <input type="hidden" name="role" :value="roleValue.id" />
        <input type="hidden" name="email" :value="form.email" />
        <input type="hidden" name="is_super_admin" :value="form.is_super_admin || Number(0)" />

        <div class="row">
          <div class="col-lg-6 pr-3 pl-3">
            <b-form-group
              id="fieldset-name"
              label-for="name"
              label-size="lg"
              label-class="font-weight-bold pb-0"
              class="required"
            >
              <template slot="label">
                <span v-text="$t('users.name')" />
              </template>
              <b-form-input
                id="name"
                name="name"
                v-model.trim="form.name"
                :placeholder="$t('users.enter_name')"
                required
              />
              <div v-if="hasError('name')">
                <div
                  class="alert alert-danger mt-1"
                  v-if="show_errors"
                  v-text="$t('commons.required')"
                ></div>
              </div>
            </b-form-group>
          </div>
          <div class="col-lg-6 pr-3">
            <b-form-group
              id="fieldset-email"
              label-for="email"
              label-size="lg"
              label-class="font-weight-bold pb-0"
              class="required"
            >
              <template slot="label">
                <span v-text="$t('users.email')" />
              </template>
              <b-form-input
                id="email"
                name="user-form-email"
                v-model.trim="form.email"
                maxlength="200"
                :placeholder="$t('users.enter_email')"
                :disabled="currentUser != null"
                class="required"
              />
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6 pr-3">
            <b-form-group
              id="is_admin"
              label-for="is_admin"
              label-size="lg"
              label-class="font-weight-bold pb-0"
            >
              <template slot="label">
                <span>Admin</span>
              </template>

              <toggle-button
               v-model="isSuperAdmin"
               :labels="{ checked: true, unchecked: false }"
               :width="55"
               :sync="true"
              >
              </toggle-button>
            </b-form-group>
          </div>
        </div>

        <div class="row" v-show="!form.is_super_admin || form.is_super_admin == 0">
          <div class="col-lg-6 pr-3">
            <b-form-group
              id="fieldset-role"
              label-for="is_super_role"
              label-size="lg"
              label-class="font-weight-bold pb-0"
              class="required"
            >
              <template slot="label">
                <span v-text="$t('users.role')" />
              </template>
              <vue-multiselect
                v-model="form.role"
                :options="roles"
                :placeholder="$t('roles.select_role')"
                track-by="id"
                label="name"
                :allow-empty="false"
                :selectLabel="''"
                :deselectLabel="''"
                :selectedLabel="''"
                style="line-height: 1.4"
                @input="onSelect"
              ></vue-multiselect>
            </b-form-group>
          </div>
        </div>

        <b-form-group>
          <div class="row justify-content-end align-items-end w-100">
            <b-button
              type="submit"
              name="user-form-submit"
              class="mr-2"
              variant="primary"
              v-text="save_or_update"
              :disabled="saving"
            ></b-button>
            <b-button
              class="btn btn-danger"
              :href="$routes('users.index')"
              v-text="$t('buttons.cancel')"
              :disabled="saving"
            ></b-button>
          </div>
        </b-form-group>
      </b-form>
    </div>
  </section>
</template>

<script>
export default {
  props: {
    currentUser: Object,
    roles: Object | Array,
    role: Object | null,
    oldInput: Object | Array,
  },
  data() {
    return {
      is_admin: false,
      csrf: null,
      save_or_update: this.$t("buttons.save"),
      form: {
        name: null,
        email: null,
        role: null,
        is_super_admin: null
      },
      errors: {
        userError: false,
      },
      show_errors: false,
      saving: false,
      roleValue: {
        id: null,
      },
    };
  },
  computed: {
    isSuperAdmin: {
      get() {
        return this.form.is_super_admin == 1;
      },
      set(value) {
        this.form.is_super_admin = Number(value);
      },
    },
    method() {
      return this.currentUser && this.currentUser.id ? "PUT" : "POST";
    },
    action() {
      return this.currentUser && this.currentUser.id ?
        this.$routes("users.update", { user: this.currentUser.id })
        : this.$routes("users.store");
    }
  },
  mounted() {
    if (this.currentUser) {
      if(this.role) {
        this.form.role = this.role;
        this.roleValue.id = this.role.id;
      }
      this.form.email = this.currentUser.email;
      this.form.is_super_admin = this.currentUser.is_super_admin
    }
  },
  created() {
    this.csrf = document.head.querySelector('meta[name="csrf-token"]').content;
    if (!_.isEmpty(this.oldInput)) {
      this.form = Object.assign({}, this.oldInput);
    } else if (this.currentUser) {
      this.save_or_update = this.$t("buttons.update");

      // For Edit Mode Set the current setup.
      this.updateSetup();
    }
  },
  methods: {
    onSubmit(e) {
      this.show_errors = true;
      if (
        Object.keys(this.errors).length != 1 ||
        this.errors["userError"] != false
      ) {
        this.$toast.error("Error", "There are errors on the form");
        e.preventDefault();
        return;
      }
      this.saving = true;
      return false;
    },
    updateSetup() {
      this.form.name = this.currentUser.name;
      this.form.email = this.currentUser.email;
      this.form.is_super_admin = this.currentUser.is_super_admin == 1;
    },
    hasError(field) {
      if (!this.form[field] || this.form[field].length == 0) {
        this.errors[field] = true;
        return true;
      } else {
        delete this.errors[field];
        return false;
      }
    },
    onSelect(e) {
      if(this.form.role) {
        this.form.role = e;
      }

      if(this.form.role) {
        this.roleValue.id = e.id;
      }
    },
  },
};
</script>
